import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Form, Input, Radio, Row, Spin } from 'antd';
import moment from 'moment';

import { YES_NO_OPTIONS } from '@/contants/common';
import { IANAToWindows } from '@/contants/date';
import { setAppLoader } from '@/redux/common/CommonActions';
import { useGetDefaultCustomerSettings } from '@/redux/hooks/useCustomerSettings';
import { getLocalDateTime, getLocalTimeZone } from '@/utils/date';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import useIs12HoursTimeFormat from '../../../../../../redux/hooks/useIs12HoursTimeFormat';
import useShipmentEditPermission from '../../../../../../redux/hooks/useShipmentEditPermission';
import {
  getCustomerRoutingSettingsInProgress,
  shipmentSpotBidInProgress,
} from '../../../../../../redux/shipment/ShipmentActions';
import { valueToNumber, valueToString } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import FormCheckBox from '../../../../../components/Inputs/FormCheckBox';
import FormDatePicker from '../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../components/Inputs/FormInput';
import FormTimePicker from '../../../../../components/Inputs/FormTimePicker';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../components/Labels/FormLabel';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import SpotBidExpireTimeSelector from '../../../../../components/Selectors/SpotBidExpireTimeSelector';
import TimeZoneSelectorTMS from '../../../../../components/Selectors/TimeZoneSelectorTMS';
import { useGetSpotBidExpirationTimeOptions } from '../../../hooks';
import SportBidRouting from './SportBidRouting';

const SPOT_BID_OPTIONS = {
  HOURS: 'Hours',
  TIME: 'Time',
};

const getPositiveNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : null;
};

const SportBidForm = ({ handleSpotBidSuccess, submitSpotBidButtonContainerClassName }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const { shipmentDetails, customerRoutingsSettings } = useSelector((state) => state.shipment);
  const { user, selectedCompany } = useSelector((state) => state.auth);
  const { data: defaultShipmentSettings } = useGetDefaultCustomerSettings(selectedCompany?.companyId || 0, 'Shipment');
  const hasEditPermission = useShipmentEditPermission();
  const is12HoursFormat = useIs12HoursTimeFormat();
  const { data: expireTimeOptions } = useGetSpotBidExpirationTimeOptions({ CustomerKey: selectedCompany?.companyId });

  const [selectedProviders, setSelectedProviders] = useState([]);
  const [lspSearchValue, setLSPSearchValue] = useState('');
  const submitButtonRef = useRef(null);
  const optionValueText = Form.useWatch('OptionValueText', form);

  const spotBidButtonDisabled = useMemo(() => selectedProviders.length === 0, [selectedProviders]);
  const isShowLSPStationCode =
    defaultShipmentSettings?.find((e) => e.parameterName === 'ShowLSPStationCode')?.defaultValue?.toUpperCase() ===
    YES_NO_OPTIONS?.[0]?.value;
  const defaultSpotBidHours = defaultShipmentSettings?.find((e) => e.parameterName === 'IsSetDefaultBidDateHour');
  const defaultBidExpiration = defaultShipmentSettings?.find(
    (e) => e.parameterName === 'AllowDefaultBidExpirationDateHour',
  );

  const handleSelectedSpotProviders = (data) => {
    setSelectedProviders(data);
  };

  const handleSubmit = (data) => {
    dispatch(setAppLoader(true));
    const carrierKey = String(shipmentDetails?.data?.carrierKey).length > 0 ? shipmentDetails?.data?.carrierKey : 0;
    const payload = {};
    payload.shipmentKey = shipmentDetails.data?.shipmentKey;
    payload.carrierKey = String(carrierKey);
    payload.rateShopCost = valueToString(shipmentDetails.data?.rateShopCost);
    payload.isReset = String(Number(Boolean(data.isReset)));
    payload.isSendMail = Number(!data.isSendMail);
    payload.spotBidTime = valueToString(data.spotBidTime);
    payload.spotBidTimeText = valueToString(data.spotBidTimeText);
    if (data.OptionValueText === SPOT_BID_OPTIONS.TIME) {
      payload.tenderDateTime = `${data.timeTenderDate.format('MM/DD/yyyy')} ${data.timeTenderTime.format('h:mm A')}`;
      payload.tenderDate = '';
    } else {
      payload.tenderDate = data.tenderDate?.format('MM/DD/yyyy');
      payload.tenderDateTime = '';
    }
    payload.userId = user.data.id;
    payload.customerKey = shipmentDetails.data?.customerKey;
    payload.spotBidTimeZoneValue = valueToString(data.SpotBidTimeZoneValue);
    payload.optionValueText = valueToString(data.OptionValueText);
    payload.spotBidType = null;
    payload.spotBidCarrierEmailList = selectedProviders.map((row) => ({
      carrierKey: row.carrierKey,
      emailAddress: row.tenderEmailAddress,
    }));
    payload.routingType = Number(data.routingType);
    payload.targetPrice = valueToNumber(data.targetPrice);

    const formData = new FormData();
    for (const key in payload) {
      if (Object.hasOwn(payload, key)) {
        formData.append(key, payload[key]);
      }
    }
    payload.callBackFun = () => {
      handleSpotBidSuccess();
    };
    dispatch(shipmentSpotBidInProgress(payload));
  };

  const handleRadioChange = ({ target: { value } }) => {
    form.setFieldsValue({ OptionValueText: value });
  };

  const handleLSPSearch = (event) => {
    setLSPSearchValue(event?.target?.value || '');
  };

  const applyDefaultBidDateSpotBid = useCallback(
    (minutes) => {
      form.setFieldsValue({
        OptionValueText: SPOT_BID_OPTIONS.HOURS,
        spotBidTime: String(minutes),
        tenderDate: moment(getLocalDateTime()),
      });
    },
    [form],
  );

  const applyBidExpirationDateHour = useCallback(
    (hours) => {
      const expiration = moment(getLocalDateTime()).add(hours, 'hours');
      const minutesSinceMidnight = expiration.hours() * 60 + expiration.minutes();
      const option = expireTimeOptions.reduce((closest, current) => {
        const currentDiff = Math.abs(Number(current.value) - minutesSinceMidnight);
        const closestDiff = Math.abs(Number(closest.value) - minutesSinceMidnight);

        return currentDiff < closestDiff ? current : closest;
      });

      form.setFieldsValue({
        OptionValueText: SPOT_BID_OPTIONS.HOURS,
        SpotBidTimeZoneValue: IANAToWindows(getLocalTimeZone()),
        spotBidTime: option?.value,
        spotBidTimeText: option?.label,
        tenderDate: expiration.clone().startOf('day'),
        timeTenderDate: expiration,
        timeTenderTime: expiration,
      });
    },
    [form, expireTimeOptions],
  );

  const initializeDefaultSpotBid = useCallback(() => {
    const defaultBidDateMinutes = getPositiveNumber(defaultSpotBidHours?.defaultValue);
    const defaultBidExpirationDateHour = getPositiveNumber(defaultBidExpiration?.defaultValue);

    if (defaultBidDateMinutes && defaultBidExpirationDateHour) {
      showNotification({
        message:
          'Check the Customer Default Settings. Both flags cannot be enabled simultaneously to auto-set the default Spot Bid Time.',
        type: NOTIFICATION_TYPES.ERROR,
      });

      return;
    }

    if (defaultBidExpirationDateHour) {
      applyBidExpirationDateHour(defaultBidExpirationDateHour);

      return;
    }

    if (defaultBidDateMinutes) {
      applyDefaultBidDateSpotBid(defaultBidDateMinutes);
    }
  }, [defaultBidExpiration, defaultSpotBidHours, applyDefaultBidDateSpotBid, applyBidExpirationDateHour]);

  useEffect(() => {
    if (!defaultShipmentSettings || !expireTimeOptions) {
      return;
    }

    initializeDefaultSpotBid();
  }, [defaultShipmentSettings, expireTimeOptions, initializeDefaultSpotBid]);

  useEffect(() => {
    if (!customerRoutingsSettings?.success && selectedCompany?.companyId) {
      dispatch(
        getCustomerRoutingSettingsInProgress({
          Customerkey: selectedCompany?.companyId,
        }),
      );
    }
  }, [customerRoutingsSettings?.success, selectedCompany, dispatch]);

  useEffect(() => {
    if (customerRoutingsSettings?.success) {
      form.setFieldsValue({
        adminEmail: customerRoutingsSettings?.data?.adminEmail,
      });
    }
  }, [customerRoutingsSettings, form]);

  return (
    <Form
      onFinish={handleSubmit}
      form={form}
      className="h-100 d-flex flex-direction-column"
      initialValues={{ routingType: 0 }}
    >
      <div className="py-15">
        <Row className="mb-10" gutter={15}>
          <Col sm={24} md={6}>
            <Spin size="small" spinning={customerRoutingsSettings?.progressing}>
              <FormInput
                label={t('translation:shipmentAdd.rateShopDetails.adminEmail')}
                editMode={false}
                name="adminEmail"
                margin="m-0"
              />
            </Spin>
          </Col>
          <Col sm={24} md={6}>
            <FormCheckBox
              checkBoxClass="normal-checkbox"
              label={t('translation:shipmentAdd.spotBid.doNotSendEmail')}
              name="isSendMail"
            />
          </Col>
          <Col sm={24} md={6}>
            <FormCheckBox
              checkBoxClass="normal-checkbox"
              label={t('translation:shipmentAdd.spotBid.resetSpotBid')}
              name="isReset"
            />
          </Col>
        </Row>
        <Radio.Group className="w-100" value={optionValueText}>
          <Row gutter={15} className="position-relative">
            <FormLabel className=" mb-15 spot-bid-expiry-time-label">
              {t('translation:shipmentAdd.spotBid.expirationTime.label')}
            </FormLabel>
            <Col sm={24} md={6}>
              <div className="mt-20 d-flex align-items-center">
                <Radio name="OptionValueText" onChange={handleRadioChange} value={SPOT_BID_OPTIONS.HOURS}>
                  <SecondaryLabel className="mt-n1 f-12 fw-600 text-dark lh-0">
                    {t('translation:shipmentAdd.spotBid.dateHours')}
                  </SecondaryLabel>
                </Radio>
              </div>
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== SPOT_BID_OPTIONS.HOURS && 'spot-bid-disabled-row'}`}>
              <FormDatePicker
                checkBoxClass="normal-checkbox"
                label={t('translation:default.fields.createdOn.placeholder')}
                name="tenderDate"
                rules={[
                  {
                    message: t('translation:shipmentAdd.spotBid.selectDate'),
                    required: optionValueText === SPOT_BID_OPTIONS.HOURS,
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== SPOT_BID_OPTIONS.HOURS && 'spot-bid-disabled-row'}`}>
              <SpotBidExpireTimeSelector
                checkBoxClass="normal-checkbox"
                label={t('translation:default.fields.hours.placeholder')}
                name="spotBidTime"
                rules={[
                  {
                    message: t('translation:default.fields.hours.message'),
                    required: optionValueText === SPOT_BID_OPTIONS.HOURS,
                  },
                ]}
                onChange={(val) => form.setFieldsValue({ spotBidTimeText: val.label })}
              />
            </Col>
            <Col sm={24} md={6}>
              <FormInput label="Ceiling Rate" margin="m-0" name="targetPrice" editMode inputType="number" />
            </Col>
          </Row>
          <Row gutter={15}>
            <Col sm={24} md={6}>
              <div className="mt-20 d-flex align-items-center">
                <Radio name="OptionValueText" onChange={handleRadioChange} value="Time">
                  <SecondaryLabel className="mt-n1 f-12 fw-600 text-dark lh-0">
                    {t('translation:shipment.drawer.shipmentTransport.dateTime')}
                  </SecondaryLabel>
                </Radio>
              </div>
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== SPOT_BID_OPTIONS.TIME && 'spot-bid-disabled-row'}`}>
              <FormDatePicker
                label={t('translation:default.fields.createdOn.placeholder')}
                name="timeTenderDate"
                margin="m-0"
                rules={[
                  {
                    message: t('translation:shipment.drawer.shipmentTransport.requestedDateTime.date_message'),
                    required: optionValueText === SPOT_BID_OPTIONS.TIME,
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== SPOT_BID_OPTIONS.TIME && 'spot-bid-disabled-row'}`}>
              <FormTimePicker
                use12Hours={is12HoursFormat}
                label={t('translation:default.fields.selectTime')}
                name="timeTenderTime"
                editMode
                margin="m-0"
                rules={[
                  {
                    message: t('translation:shipment.drawer.shipmentTransport.requestedDateTime.timeMessage'),
                    required: optionValueText === SPOT_BID_OPTIONS.TIME,
                  },
                ]}
              />
            </Col>
            <Col sm={24} md={6} className={`${optionValueText !== SPOT_BID_OPTIONS.TIME && 'spot-bid-disabled-row'}`}>
              <TimeZoneSelectorTMS
                label={t('translation:default.fields.timeZone.placeholder')}
                name="SpotBidTimeZoneValue"
                editMode
                margin="m-0"
                rules={[
                  {
                    message: t('translation:default.fields.timeZone.message'),
                    required: optionValueText === SPOT_BID_OPTIONS.TIME,
                  },
                ]}
              />
            </Col>
          </Row>
        </Radio.Group>
        <HiddenFormInput
          name="OptionValueText"
          rules={[
            {
              message: t('translation:shipmentAdd.spotBid.expirationTime.message'),
              required: true,
            },
          ]}
        />
      </div>
      <Divider className="my-15" />
      <Row justify="space-between">
        <Col>
          <PrimaryLabel className="f-18">{t('translation:shipmentAdd.spotBid.routingSpotBidList')}</PrimaryLabel>
        </Col>
        {isShowLSPStationCode && (
          <Col>
            <Input
              placeholder="Search LSP Station Code"
              className="m-0 form-input"
              style={{ minWidth: '180px' }}
              onChange={handleLSPSearch}
            />
          </Col>
        )}
      </Row>
      <Divider className="my-15" />
      <div className="overflow-hidden h-100">
        <SportBidRouting lspSearchValue={lspSearchValue} handleSelectedSpotProviders={handleSelectedSpotProviders} />
      </div>
      <div
        style={{
          height: submitButtonRef?.current?.clientHeight ? submitButtonRef?.current?.clientHeight : 0,
        }}
      />
      <Row
        justify="end"
        className={`submit-spot-bid-button-container ${
          submitSpotBidButtonContainerClassName ? submitSpotBidButtonContainerClassName : ''
        }`}
        ref={submitButtonRef}
      >
        <Col>
          <Button disabled={spotBidButtonDisabled || !hasEditPermission} variant="success" buttonType="submit">
            {t('translation:shipmentAdd.spotBid.submitSpotBid')}
          </Button>
        </Col>
      </Row>
      <HiddenFormInput name="spotBidTimeText" />
      <HiddenFormInput name="routingType" />
    </Form>
  );
};
export default SportBidForm;
